"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  num: "01",
  title: "KOSTNADSFRITT HEMBESÖK",
  desc: "Vi går igenom badrummets förutsättningar, dina önskemål och vilka lösningar som passar projektet."
}, {
  num: "02",
  title: "PLANERING & MATERIALVAL",
  desc: "Tillsammans planerar vi badrummets utformning, material, inredning och projektets olika arbetsmoment."
}, {
  num: "03",
  title: "DEMONTERING, RIVNING & UNDERARBETE",
  desc: "Det befintliga badrummet rivs och underlag och konstruktioner förbereds för kommande installationer och våtrumsarbete."
}, {
  num: "04",
  title: "INSTALLATION & VÅTRUMSARBETE",
  desc: "VVS, el, tätskikt och plattsättning utförs av respektive yrkesgrupp och samordnas inom totalentreprenaden."
}, {
  num: "05",
  title: "FÄRDIGSTÄLLANDE & GENOMGÅNG",
  desc: "Badrummet färdigställs, installationer och detaljer kontrolleras och projektet avslutas med en genomgång."
}];
export default function Renovationprocesssteps() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="253224" id="renovationprocesssteps" className="bg-background text-foreground py-20 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h2 className="text-3xl md:text-5xl font-semibold text-center mb-16 md:mb-24 font-default tracking-tight">Så går det till att renovera badrum i Karlstad - steg för steg</h2>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-12 md:gap-8">
            {steps.map((step, i) => <div key={i} data-index={i} className="flex flex-col relative group">
                {}
                <div className="h-px w-full bg-border mb-6 md:mb-8 transition-colors duration-500 group-hover:bg-primary/30" />

                {}
                <div className="flex flex-col pr-4">
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-primary font-default text-xl md:text-2xl font-medium tracking-tight">
                      {step.num}
                    </span>
                    <span className="text-muted-foreground/30 font-default text-xl md:text-2xl font-light">
                      /
                    </span>
                    <h3 className="font-semibold font-default uppercase tracking-wider text-foreground !text-base">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-muted-foreground text-sm md:text-base leading-relaxed font-default">
                    {step.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}